import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

function getHtml2CanvasOptions() {
  return {
    scale: Math.min(2, typeof window !== "undefined" ? window.devicePixelRatio || 2 : 2),
    useCORS: true,
    allowTaint: false,
    logging: false,
    backgroundColor: "#ffffff",
  };
}

/** @param {string} prefix e.g. "dashboard", "total-sales" */
export function buildTimestampedPngFilename(prefix) {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${prefix}-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}.png`;
}

/** @param {string} prefix */
export function buildTimestampedPdfFilename(prefix) {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${prefix}-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}.pdf`;
}

/**
 * @param {HTMLElement} element
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function captureElementToCanvas(element) {
  return html2canvas(element, getHtml2CanvasOptions());
}

/**
 * Rasterize a DOM subtree and trigger a browser download as PNG.
 * @param {HTMLElement | null | undefined} element
 * @param {string} filename — should end with `.png`
 * @returns {Promise<void>}
 */
export async function downloadElementAsPng(element, filename) {
  if (!element || !(element instanceof HTMLElement)) {
    return;
  }

  const canvas = await captureElementToCanvas(element);
  const url = canvas.toDataURL("image/png");
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  anchor.click();
}

/**
 * Same visual as PNG, embedded in A4 PDF (multi-page if the capture is taller than one page).
 * @param {HTMLElement | null | undefined} element
 * @param {string} filename — should end with `.pdf`
 * @returns {Promise<void>}
 */
export async function downloadElementAsPdf(element, filename) {
  if (!element || !(element instanceof HTMLElement)) {
    return;
  }

  const canvas = await captureElementToCanvas(element);
  const marginMm = 8;
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const usableW = pageW - 2 * marginMm;
  const usableH = pageH - 2 * marginMm;

  const fullWidthMm = usableW;
  const fullHeightMm = (canvas.height / canvas.width) * fullWidthMm;

  if (fullHeightMm <= usableH + 0.5) {
    const imgData = canvas.toDataURL("image/png");
    const x = marginMm + (usableW - fullWidthMm) / 2;
    const y = marginMm + (usableH - fullHeightMm) / 2;
    pdf.addImage(imgData, "PNG", x, y, fullWidthMm, fullHeightMm, undefined, "FAST");
    pdf.save(filename);
    return;
  }

  /** Slice tall captures into page-sized horizontal strips (px → mm width fixed). */
  const sliceHeightPx = Math.max(1, Math.floor((usableH / fullHeightMm) * canvas.height));
  let yPx = 0;
  let first = true;

  while (yPx < canvas.height) {
    const hPx = Math.min(sliceHeightPx, canvas.height - yPx);
    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = hPx;
    const ctx = sliceCanvas.getContext("2d");
    if (!ctx) break;
    ctx.drawImage(canvas, 0, yPx, canvas.width, hPx, 0, 0, canvas.width, hPx);

    const sliceData = sliceCanvas.toDataURL("image/png");
    const sliceHmm = (hPx / canvas.height) * fullHeightMm;

    if (!first) {
      pdf.addPage();
    }
    first = false;
    pdf.addImage(sliceData, "PNG", marginMm, marginMm, fullWidthMm, sliceHmm, undefined, "FAST");

    yPx += hPx;
  }

  pdf.save(filename);
}
